import { useEffect, useRef, useState } from 'react';
import {
  BOOTH_RECENT_EVIDENCE_CAMERA_ID,
  BOOTH_RECENT_EVIDENCE_DEVICE_ID,
  BOOTH_RECENT_EVIDENCE_POLICY_ID,
  BOOTH_RECENT_EVIDENCE_SITE_ID,
  BOOTH_RECENT_EVIDENCE_TIME_ZONE,
  BOOTH_RECENT_EVIDENCE_ZONE_ID,
  boothRecentEvidenceDayBoundary,
  buildBoothRecentEvidenceItems,
  groupBoothEvidenceOccurrences,
  type BoothRecentEvidenceEpisode,
  type BoothRecentEvidenceView,
} from '../domain/showcase/boothRecentEvidence';
import { api } from '../services/api';

export const BOOTH_RECENT_EVIDENCE_POLL_MS = 10_000;
export const BOOTH_RECENT_EVIDENCE_TIMEOUT_MS = 2_750;

type RecentEvidencePayload = {
  ok?: boolean;
  episodes?: Array<{
    episode_id?: string;
    qualified_at?: string;
    started_at?: string | null;
    ended_at?: string | null;
    status?: string | null;
    parent_incident_id?: string | null;
    physical_occurrence_id?: string | null;
    evidence_state?: string | null;
    duration_ms?: number | null;
    site_id?: string | null;
    device_id?: string | null;
    camera_id?: string | null;
    zone_id?: string | null;
    policy_id?: string | null;
  }>;
};

export function useShowcaseBoothRecentEvidence(args: {
  siteId?: string | null;
  conditionId?: string | null;
  deviceId?: string | null;
  cameraId?: string | null;
  zoneId?: string | null;
  policyId?: string | null;
  timeZone?: string | null;
  selectedLocalDay?: string | null;
  zoneLabel?: string | null;
  cameraLabel?: string | null;
}): BoothRecentEvidenceView {
  const siteId = String(args.siteId || BOOTH_RECENT_EVIDENCE_SITE_ID).trim();
  const conditionId = String(args.conditionId || '').trim();
  const deviceId = String(args.deviceId || BOOTH_RECENT_EVIDENCE_DEVICE_ID).trim();
  const cameraId = String(args.cameraId || BOOTH_RECENT_EVIDENCE_CAMERA_ID).trim();
  const zoneId = String(args.zoneId || BOOTH_RECENT_EVIDENCE_ZONE_ID).trim();
  const policyId = String(args.policyId || BOOTH_RECENT_EVIDENCE_POLICY_ID).trim();
  const timeZone = args.timeZone || BOOTH_RECENT_EVIDENCE_TIME_ZONE;
  const selectedLocalDay = String(args.selectedLocalDay || '').trim();
  const zoneLabel = args.zoneLabel;
  const cameraLabel = args.cameraLabel;
  const [view, setView] = useState<BoothRecentEvidenceView>({ status: 'loading', items: [], occurrences: [], recordedCount: 0 });
  const inFlightRef = useRef(false);
  const requestGenerationRef = useRef(0);
  const requestTimeoutRef = useRef<number | undefined>();

  useEffect(() => {
    let active = true;
    if (!conditionId) {
      setView({ status: 'unavailable', items: [], occurrences: [], recordedCount: 0 });
      return undefined;
    }

    const load = async () => {
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      const requestGeneration = requestGenerationRef.current + 1;
      requestGenerationRef.current = requestGeneration;
      const boundary = boothRecentEvidenceDayBoundary(Date.now(), timeZone, selectedLocalDay);
      const params = new URLSearchParams({
        device_id: deviceId,
        camera_id: cameraId,
        zone_id: zoneId,
        policy_id: policyId,
        from: boundary.occurredFrom,
        to: boundary.occurredTo,
      });
      const request = api.get(
        `/api/v1/sites/${encodeURIComponent(siteId)}/conditions/${encodeURIComponent(conditionId)}/recent-evidence-episodes?${params.toString()}`,
      );
        if (!request || typeof request.then !== 'function') {
        inFlightRef.current = false;
        if (active) setView({ status: 'unavailable', items: [], occurrences: [], recordedCount: 0 });
        return;
      }
      const timeoutRequest = new Promise<never>((_, reject) => {
        const timeout = window.setTimeout(() => {
          if (active && requestGenerationRef.current === requestGeneration) {
            requestGenerationRef.current += 1;
            setView((current) => (
              current.items.length > 0 ? current : { status: 'unavailable', items: [], occurrences: [], recordedCount: 0 }
            ));
          }
          reject(new Error('booth_recent_evidence_timeout'));
        }, BOOTH_RECENT_EVIDENCE_TIMEOUT_MS);
        requestTimeoutRef.current = timeout;
      });
      void request.then(
        () => {
          inFlightRef.current = false;
          if (requestTimeoutRef.current != null) {
            window.clearTimeout(requestTimeoutRef.current);
            requestTimeoutRef.current = undefined;
          }
        },
        () => {
          inFlightRef.current = false;
          if (requestTimeoutRef.current != null) {
            window.clearTimeout(requestTimeoutRef.current);
            requestTimeoutRef.current = undefined;
          }
        },
      );
      try {
        const response = await Promise.race([request, timeoutRequest]);
        if (!active || requestGenerationRef.current !== requestGeneration) return;
        const payload = (response?.data || {}) as RecentEvidencePayload;
        if (payload.ok !== true) throw new Error('recent_evidence_not_ok');
        const episodes: BoothRecentEvidenceEpisode[] = (payload.episodes || []).map((episode) => ({
          episodeId: String(episode.episode_id || ''),
          qualifiedAt: String(episode.qualified_at || ''),
          startedAt: episode.started_at,
          endedAt: episode.ended_at,
          status: episode.status,
          parentIncidentId: episode.parent_incident_id,
          physicalOccurrenceId: episode.physical_occurrence_id,
          evidenceState: episode.evidence_state,
          durationMs: episode.duration_ms,
          siteId: episode.site_id || siteId,
          deviceId: episode.device_id || deviceId,
          cameraId: episode.camera_id || cameraId,
          zoneId: episode.zone_id || zoneId,
          policyId: episode.policy_id || policyId,
        }));
        const recorded = buildBoothRecentEvidenceItems(episodes, {
          timeZone,
          siteId,
          deviceId,
          cameraId,
          zoneId,
          policyId,
          zoneLabel,
          cameraLabel,
          limit: null,
          ...boundary,
        });
        const items = recorded.slice(0, 2);
        setView({
          status: recorded.length > 0 ? 'ready' : 'empty',
          items,
          occurrences: groupBoothEvidenceOccurrences(recorded),
          recordedCount: recorded.length,
        });
      } catch {
        if (!active || requestGenerationRef.current !== requestGeneration) return;
        setView((current) => (
          current.items.length > 0 ? current : { status: 'unavailable', items: [], occurrences: [], recordedCount: 0 }
        ));
      }
    };

    void load();
    const interval = window.setInterval(() => { void load(); }, BOOTH_RECENT_EVIDENCE_POLL_MS);
    return () => {
      active = false;
      requestGenerationRef.current += 1;
      window.clearInterval(interval);
      if (requestTimeoutRef.current != null) {
        window.clearTimeout(requestTimeoutRef.current);
        requestTimeoutRef.current = undefined;
      }
    };
  }, [cameraId, cameraLabel, conditionId, deviceId, policyId, selectedLocalDay, siteId, timeZone, zoneId, zoneLabel]);

  return view;
}
